import { type RoomMember, RoomMemberEvent } from 'matrix-js-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useMatrixContext } from '../MatrixContext';

/**
 * @param roomId - Die ID des Rooms
 * @returns Array von RoomMember-Objekten die gerade tippen (ohne den eigenen User)
 */
export function useTypingUsers(roomId: string | undefined): RoomMember[] {
  const { client } = useMatrixContext();
  const [typingUsers, setTypingUsers] = useState<RoomMember[]>([]);

  const handleTypingEvent = useCallback(
    (event: unknown, member: RoomMember) => {
      if (!client || !roomId) return;

      const room = client.getRoom(roomId);
      if (!room) return;

      // Hole alle tippenden User außer dem eigenen
      const myUserId = client.getUserId();
      const typing = room.getMembersWithMembership('join').filter((m) => {
        if (m.userId === myUserId) return false;
        return m.typing;
      });

      setTypingUsers(typing);
    },
    [client, roomId],
  );

  useEffect(() => {
    if (!client || !roomId) {
      setTypingUsers([]);
      return;
    }

    // Initial check
    const room = client.getRoom(roomId);
    if (room) {
      const myUserId = client.getUserId();
      const typing = room.getMembersWithMembership('join').filter((m) => {
        if (m.userId === myUserId) return false;
        return m.typing;
      });
      setTypingUsers(typing);
    }

    // Subscribe to typing events
    client.on(RoomMemberEvent.Typing, handleTypingEvent);

    return () => {
      client.off(RoomMemberEvent.Typing, handleTypingEvent);
    };
  }, [client, roomId, handleTypingEvent]);

  return typingUsers;
}

export function useIsTyping(roomId: string | undefined): boolean {
  const typingUsers = useTypingUsers(roomId);
  return typingUsers.length > 0;
}
